import { useMemo } from 'react';
import qs from 'query-string';
import { useCustomHistory } from '../../router/navigation';
export type PrimitiveFilterValue = string | number | boolean;
export type FilterArrayValue =
  | PrimitiveFilterValue[]
  | PrimitiveFilterValue[][];
export type FilterValue =
  | PrimitiveFilterValue
  | FilterArrayValue
  | null
  | undefined;
export type FiltersState = Record<string, FilterValue>;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

const isSerializedObject = (value: string) =>
  (value.startsWith('{') && value.endsWith('}')) ||
  (value.startsWith('[') && value.endsWith(']'));

const parseSerializedValue = (value: string) => {
  if (!isSerializedObject(value)) return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const normalizeQueryValue = (
  value: string | string[] | null
): FilterValue | undefined => {
  if (value == null) return undefined;

  if (Array.isArray(value)) {
    const parsed = value
      .map((item) => parseSerializedValue(item))
      .filter((item) => item != null && item !== '');
    return parsed.length > 0 ? (parsed as FilterValue) : undefined;
  }

  if (!isNonEmptyString(value)) return undefined;

  return parseSerializedValue(value) as PrimitiveFilterValue;
};

const serializeFilterValue = (
  value: FilterValue
): string | string[] | undefined => {
  if (value == null) return undefined;

  if (Array.isArray(value)) {
    const serializedArray = value
      .map((item) => {
        if (item == null || item === '') return null;
        if (Array.isArray(item)) return JSON.stringify(item);
        return String(item);
      })
      .filter((item): item is string => item != null && item !== '');

    return serializedArray.length > 0 ? serializedArray : undefined;
  }

  return value === '' ? undefined : String(value);
};

export const parseFiltersFromSearch = (search: string) => {
  const parsed = qs.parse(search);

  return Object.entries(parsed).reduce((acc, [key, value]) => {
    const normalizedValue = normalizeQueryValue(value);

    if (normalizedValue == null) return acc;

    return {
      ...acc,
      [key]: normalizedValue,
    };
  }, {});
};

export const stringifyFiltersToSearch = (filters: FiltersState): string => {
  const queryParams = Object.entries(filters || {}).reduce<
    Record<string, string | string[]>
  >((acc, [key, value]) => {
    const serialized = serializeFilterValue(value);

    if (serialized == null) return acc;

    return {
      ...acc,
      [key]: serialized,
    };
  }, {});

  return qs.stringify(queryParams);
};

export const useFilterQuery = () => {
  const history = useCustomHistory();

  const queryFilters = useMemo(
    () => parseFiltersFromSearch(history.location.search),
    [history.location.search]
  );

  const syncFiltersToQuery = (filters: FiltersState) => {
    const queryString = stringifyFiltersToSearch(filters);
    const nextSearch = queryString ? `?${queryString}` : '';

    if (nextSearch === history.location.search) return;
    const latestHistoryState =
      window.history.state?.usr ?? history.location.state;
    history.replace(
      `${history.location.pathname}${nextSearch}`,
      latestHistoryState
    );
  };

  return {
    search: history.location.search,
    queryFilters,
    syncFiltersToQuery,
  };
};
